import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, Linkedin, Send, CheckCircle2 } from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/layout/PageHero";
import { useLang } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";

const sectors = ["Government", "Defence", "Space", "Healthcare", "Energy", "Finance", "Industry", "Research", "Other"];

export default function Contact() {
  const { tr } = useLang();
  const [form, setForm] = useState({ name: "", email: "", organization: "", role: "", sector: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success(tr("Message sent. Our team will be in touch shortly."));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || tr("Could not send message."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title={tr("Contact")}
        description={tr("Contact the AstroLife team to discuss verifiable AI governance for your organisation.")}
        path="/contact"
      />

      <PageHero
        overline={tr("Contact")}
        title={tr("Let's discuss verifiable AI governance.")}
        subtitle={tr("Reach out to explore how AstroLife can support your organisation's AI governance, compliance and sovereignty objectives.")}
        breadcrumbs={[{ label: tr("Contact") }]}
      />

      <section className="bg-navy py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12">
            {/* Info */}
            <div className="lg:col-span-4">
              <div className="space-y-6">
                <a href="mailto:syntia@astrolife.es" className="flex items-start gap-4 rounded-2xl border border-navy-border bg-navy-surface p-6 hover:border-astro-blue/40 transition-colors">
                  <Mail className="h-6 w-6 text-astro-light" />
                  <div>
                    <p className="font-heading font-semibold text-white">{tr("Corporate email")}</p>
                    <p className="mt-1 text-sm text-silver/60">syntia@astrolife.es</p>
                  </div>
                </a>
                <a href="https://www.linkedin.com/company/astrolife" target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin" className="flex items-start gap-4 rounded-2xl border border-navy-border bg-navy-surface p-6 hover:border-astro-blue/40 transition-colors">
                  <Linkedin className="h-6 w-6 text-astro-light" />
                  <div>
                    <p className="font-heading font-semibold text-white">LinkedIn</p>
                    <p className="mt-1 text-sm text-silver/60">{tr("Follow AstroLife")}</p>
                  </div>
                </a>
                <div className="flex items-start gap-4 rounded-2xl border border-navy-border bg-navy-surface p-6">
                  <MapPin className="h-6 w-6 text-astro-light" />
                  <div>
                    <p className="font-heading font-semibold text-white">{tr("Location")}</p>
                    <p className="mt-1 text-sm text-silver/60">{tr("European Union")}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-8">
              {done ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center rounded-2xl border border-astro-blue/30 bg-navy-surface p-16 text-center"
                  data-testid="contact-success"
                >
                  <CheckCircle2 className="h-14 w-14 text-astro-light" />
                  <h2 className="mt-6 font-heading text-2xl font-bold text-white">{tr("Thank you")}</h2>
                  <p className="mt-3 max-w-md text-silver/60">{tr("Your message has been received. Our team will respond shortly.")}</p>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="rounded-2xl border border-navy-border bg-navy-surface p-8 md:p-10" data-testid="contact-form">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label={tr("Full name")} required>
                      <input required value={form.name} onChange={set("name")} data-testid="contact-name" className={inputCls} />
                    </Field>
                    <Field label={tr("Email")} required>
                      <input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email" className={inputCls} />
                    </Field>
                    <Field label={tr("Organization")}>
                      <input value={form.organization} onChange={set("organization")} data-testid="contact-org" className={inputCls} />
                    </Field>
                    <Field label={tr("Role")}>
                      <input value={form.role} onChange={set("role")} data-testid="contact-role" className={inputCls} />
                    </Field>
                    <Field label={tr("Sector")} className="sm:col-span-2">
                      <select value={form.sector} onChange={set("sector")} data-testid="contact-sector" className={inputCls}>
                        <option value="">{tr("Select a sector")}</option>
                        {sectors.map((s) => <option key={s} value={s}>{tr(s)}</option>)}
                      </select>
                    </Field>
                    <Field label={tr("Message")} required className="sm:col-span-2">
                      <textarea required rows={5} value={form.message} onChange={set("message")} data-testid="contact-message" className={inputCls} />
                    </Field>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-astro-blue px-8 py-4 font-semibold text-white hover:bg-astro-light transition-colors disabled:opacity-60"
                  >
                    {submitting ? tr("Sending…") : tr("Send message")} <Send className="h-4 w-4" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const inputCls =
  "w-full rounded-xl border border-navy-border bg-navy px-4 py-3 text-sm text-white placeholder:text-silver/40 focus:border-astro-blue focus:outline-none focus:ring-2 focus:ring-astro-blue/40 transition-colors";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-2 block text-sm font-medium text-silver/80">
        {label} {required && <span className="text-astro-light">*</span>}
      </span>
      {children}
    </label>
  );
}
